import { useTranslation } from 'react-i18next';
import aboutTeam from '../assets/about-team.jpg';
import { CheckCircle } from 'lucide-react';
import { useCMS, pickLoc } from '../context/CMSContext';
import { useSeo, breadcrumb } from '../lib/seo';

/** Normalise an i18n value that may be an array of points (new) or a plain
 *  string (legacy data) into a renderable list. */
function toList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

const About = () => {
  const { t } = useTranslation();
  const { cms, lang } = useCMS();

  const visionPoints = toList(t('visionMission.visionDesc', { returnObjects: true }));
  const missionPoints = toList(t('visionMission.missionDesc', { returnObjects: true }));

  const seo = cms.seo.pages.about;

  useSeo({
    title: pickLoc(seo.title, lang),
    description: pickLoc(seo.description, lang),
    keywords: pickLoc(seo.keywords, lang),
    path: '/about',
    jsonLd: breadcrumb([
      { name: '首頁', path: '/' },
      { name: t('nav.about'), path: '/about' },
    ]),
  });

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Banner */}
      <div className="bg-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-serif">{t('nav.about')}</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {visionPoints.join(' ')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <img
              src={aboutTeam}
              alt="Our Team"
              className="rounded-lg shadow-xl w-full h-auto object-cover"
            />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-navy-900 mb-6 font-serif">{t('visionMission.missionTitle')}</h2>
            <ul className="space-y-4 mb-4">
              {missionPoints.map((pt, i) => (
                <li key={i} className="flex items-start">
                  <span className="mt-2.5 h-2 w-2 rounded-full bg-teal-500 flex-shrink-0 mr-4" aria-hidden="true"></span>
                  <span className="text-gray-600 text-lg leading-relaxed">{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <h2 className="text-3xl font-bold text-navy-900 mb-8 text-center font-serif">{t('whyChooseUs.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(t('whyChooseUs.items', { returnObjects: true }) as any[]).map((item: any, index: number) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="h-6 w-6 text-gold-500 flex-shrink-0 mt-1" />
                <div className="ml-4">
                  <h4 className="text-xl font-semibold text-navy-900">{item.title}</h4>
                  <p className="text-gray-600 mt-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
